/**
 * PoweredByCouncil Component
 *
 * Two exports:
 * 1. CouncilBadge - "Powered by the Council" label for left side
 * 2. ModelCarousel - Infinite scrolling carousel for right side
 *
 * Design Philosophy:
 * - Badge is prominent and readable
 * - Carousel has bigger text, elegant fade edges
 * - Models fetched dynamically from API (synced with MODEL_REGISTRY)
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useModels } from '@/hooks/useModels';
import { getProviderLogo } from '@/utils/providerUtils';

// Fallback models in case API is unavailable (matches MODEL_REGISTRY)
// Already interleaved by provider for visual variety
const fallbackModels = [
  { name: 'GPT-5', provider: 'openai' },
  { name: 'Claude Opus 4', provider: 'anthropic' },
  { name: 'Gemini 2.5 Flash', provider: 'google' },
  { name: 'DeepSeek Chat', provider: 'deepseek' },
  { name: 'GPT-4o', provider: 'openai' },
];

/**
 * Shuffle models to interleave providers (no consecutive models from same provider)
 * Uses a round-robin approach: picks one model from each provider in turn
 */
function interleaveByProvider<T extends { provider: string }>(models: T[]): T[] {
  if (models.length <= 1) return models;

  // Group models by provider
  const byProvider = new Map<string, T[]>();
  models.forEach(m => {
    const list = byProvider.get(m.provider) || [];
    list.push(m);
    byProvider.set(m.provider, list);
  });

  // Shuffle each provider's models for variety within provider
  byProvider.forEach((list) => {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
  });

  // Get providers sorted by count (descending) for better distribution
  const providers = Array.from(byProvider.keys()).sort(
    (a, b) => (byProvider.get(b)?.length || 0) - (byProvider.get(a)?.length || 0)
  );

  // Round-robin pick from each provider
  const result: T[] = [];
  let hasMore = true;
  while (hasMore) {
    hasMore = false;
    for (const provider of providers) {
      const list = byProvider.get(provider);
      if (list && list.length > 0) {
        result.push(list.shift()!);
        hasMore = hasMore || list.length > 0;
      }
    }
  }

  return result;
}

// Provider colors for subtle branding (fallback when logos not rendered)
const providerDot: Record<string, string> = {
  openai: 'text-emerald-500',
  anthropic: 'text-orange-400',
  google: 'text-blue-400',
  deepseek: 'text-purple-400',
};

interface CouncilBadgeProps {
  className?: string;
  /** Optional: pass model count externally (for when used separately from ModelCarousel) */
  modelCount?: number;
}

/**
 * CouncilBadge - Prominent "Powered by the Council" label
 * Place this on the left side under the CTA
 */
export function CouncilBadge({ className, modelCount }: CouncilBadgeProps) {
  // If no external count provided, fetch models to get the count
  const { totalCount } = useModels();
  const displayCount = modelCount ?? totalCount;

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {/* Pulsing indicator */}
      <div className="relative">
        <div className="w-2.5 h-2.5 bg-verdict-primary rounded-full animate-pulse" />
        <div className="absolute inset-0 w-2.5 h-2.5 bg-verdict-primary rounded-full animate-ping opacity-30" />
      </div>
      {/* Label */}
      <span className="text-sm uppercase tracking-widest text-text-primary font-semibold">
        Powered by the Reasoning Council
      </span>
      {/* Model count - dynamic from API */}
      <span className="text-xs text-text-muted font-mono">
        {displayCount > 0 ? `${displayCount}+ models` : '...'}
      </span>
    </div>
  );
}

interface ModelCarouselProps {
  className?: string;
}

/**
 * ModelCarousel - Infinite scrolling carousel of AI models
 * Place this on the right side
 * Models are fetched dynamically from the API (synced with MODEL_REGISTRY)
 */
export function ModelCarousel({ className }: ModelCarouselProps) {
  const { models, isLoading } = useModels();

  // Use fetched models or fallback if loading/error
  // Then interleave by provider so no consecutive models from same provider
  const displayModels = useMemo(() => {
    const raw = models.length > 0
      ? models.map((m) => ({ name: m.name, provider: m.provider }))
      : fallbackModels;
    return interleaveByProvider(raw);
  }, [models]);

  // Double the models for seamless infinite scroll
  const scrollModels = [...displayModels, ...displayModels];

  return (
    <div className={cn('w-full', className)}>
      {/* Infinite scroll carousel with CSS mask for fade edges */}
      <div
        className="overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        }}
      >
        {/* Scrolling track */}
        <div className={cn('flex animate-scroll-left', isLoading && 'opacity-50')}>
          {scrollModels.map((model, idx) => (
            <div
              key={`${model.name}-${idx}`}
              className="flex items-center gap-2.5 px-5 py-2 shrink-0"
            >
              {/* Provider logo */}
              <span
                className={cn(
                  providerDot[model.provider] || 'text-text-secondary'
                )}
              >
                {getProviderLogo(model.provider, 14)}
              </span>
              {/* Model name - BIGGER */}
              <span className="text-sm text-text-primary whitespace-nowrap font-medium">
                {model.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface PoweredByCouncilProps {
  className?: string;
}

/**
 * PoweredByCouncil - Legacy export combining both (for backwards compat)
 * @deprecated Use CouncilBadge and ModelCarousel separately
 */
export function PoweredByCouncil({ className }: PoweredByCouncilProps) {
  return (
    <div className={cn('w-full space-y-4', className)}>
      <CouncilBadge />
      <ModelCarousel />
    </div>
  );
}

